import { useEffect, useState } from 'react';
import { PageHeader } from '../../shared/components';
import { useApi } from '../../shared/hooks/useApi';
import { AccountPaths } from '../../shared/api/constants';
import { useApiHandleError } from '../../shared/hooks/useApiHandleError';
import { Card, Col, Container, ListGroup, Row, Badge, Button } from 'react-bootstrap';
import { UserProfileDto } from 'appDtos';
import { AlertComponent } from '../../shared/components';
import { formatDate, formatPhoneNumber } from '../../shared/utils/utils';
import { Loading, ChangePasswordModal } from '../../shared/components';
import { Avatar } from '../../shared/components/Avatar';
import { Icon } from '../../shared/components';
import { faLock } from '@fortawesome/free-solid-svg-icons';

export const UserProfile = () => {
  const [profile, setProfile] = useState<UserProfileDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const API = useApi();
  const { setError, errorMessage } = useApiHandleError();

  useEffect(() => {
    (async () => {
      try {
        const response = await API.get(AccountPaths.GetProfile());
        if (response.data) {
          setProfile(response.data);
        }
      } catch (error) {
        setError(error);
      }
      setLoading(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <PageHeader pageTitle='User Profile' />
      {loading ? (
        <Loading />
      ) : (
        profile ? (
          <Card className="border border-1 border-success">
            <Card.Header>
              <AlertComponent variant='danger' message={errorMessage} />
              <Row>
                <Col md="4">
                  <Avatar firstName={profile.firstName} lastName={profile.lastName}/>
                </Col>
                <Col>
                  <Card.Title className='h3 h2-md fw-bold mt-5'>{profile.fullName}</Card.Title>
                  <Card.Title className="h5 h4-md fw-bold">Email: {profile.email}</Card.Title>
                  <Card.Title className="h5 h4-md fw-bold">Phone: {profile.phoneNumber ? formatPhoneNumber(profile.phoneNumber) : 'N/A'}</Card.Title>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="4">
                  <Card.Subtitle className="mt-3">Status</Card.Subtitle>
                  <div className="h5 h4-md fw-bold">
                    {profile.isActive ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="danger">Inactive</Badge>
                    )}
                  </div>
                </Col>
                <Col md="4">
                  <Card.Subtitle className="mt-3">Created Date</Card.Subtitle>
                  <div className="h5 h4-md fw-bold">{formatDate(profile.createdDate)}</div>
                </Col>
                <Col md="4">
                  <Card.Subtitle className="mt-3">Password Changed</Card.Subtitle>
                  <div className="h5 h4-md fw-bold">
                    {profile.hasChangedDefaultPassword ? (
                      <Badge bg="success">Yes</Badge>
                    ) : (
                      <Badge bg="warning">No - Using Default</Badge>
                    )}
                  </div>
                </Col>
                <Col md="4">
                  <Card.Subtitle className="mt-3">Login Count</Card.Subtitle>
                  <div className="h5 h4-md fw-bold">{profile.loginsCount}</div>
                </Col>
                <Col md="4">
                  <Card.Subtitle className="mt-3">Last Login Date/Time</Card.Subtitle>
                  <div className="h5 h4-md fw-bold">
                    {profile.lastLoginDate ? new Date(profile.lastLoginDate).toLocaleString() : 'N/A'}
                  </div>
                </Col>
              </Row>
              <div className="my-3 border-2 p-2 rounded border border-2">
                <Card.Title className="fw-bold mb-0">ROLES</Card.Title>
              </div>
              <Card>
                <ListGroup variant="flush">
                  {profile.roles.length ? (
                    profile.roles.map((r, index) => (
                      <ListGroup.Item className="fw-bold" key={r.roleId || index}>
                        {r.name}
                      </ListGroup.Item>
                    ))
                  ) : (
                    <ListGroup.Item>You have no roles assigned.</ListGroup.Item>
                  )}
                </ListGroup>
              </Card>
            </Card.Body>
          </Card>
        ) : (
          <AlertComponent message={errorMessage} />
        )
      )}
      {!loading && profile && (
        <Button
          onClick={() => setShowPasswordModal(true)}
          variant="success"
          className="mt-5 fw-bold"
        >
          <Icon icon={faLock} /> Change Password
        </Button>
      )}
      <ChangePasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </Container>
  );
}
